import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import GoogleButton from './GoogleButton';
import AuthDivider from './AuthDivider';
import { registerWithEmail, loginWithGoogle, checkInvite } from '../../services/authService';
import { ROLE_LABELS } from '../../services/userService';
import { getSystemConfig } from '../../services/adminService';

const FEATURES = [
  { icon: '⚡', text: '6 bancos em uma simulação' },
  { icon: '📄', text: 'PDF profissional com sua marca' },
  { icon: '📊', text: 'Gráficos e comparativos detalhados' },
  { icon: '🔒', text: '7 dias grátis, sem cartão de crédito' },
];

const PLANOS = [
  { id: 'simulador',     emoji: '⚡', nome: 'Simulador',          preco: 'R$ 197/mês' },
  { id: 'simulador-crm', emoji: '🔗', nome: '+ Seu CRM',         preco: 'R$ 397/mês', popular: true },
  { id: 'completo',      emoji: '🏠', nome: '+ CRM SimulaBank',  preco: 'R$ 597/mês' },
];

const SEGMENTOS = ['Correspondente Bancário', 'Assessoria de Crédito', 'Imobiliária', 'Construtora / Incorporadora', 'Banco / Financeira', 'Outro'];

const inputStyle = {
  width: '100%', padding: '12px 16px', border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font)', fontSize: '0.9rem',
  color: 'var(--text)', background: 'var(--white)', outline: 'none', transition: 'all 0.2s',
};

export default function CadastroPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteEmail = searchParams.get('invite');

  const [plano, setPlano] = useState('simulador-crm');
  const [form, setForm] = useState({ nome: '', telefone: '', email: '', senha: '', empresa: '', segmento: '' });
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [cadastroFechado, setCadastroFechado] = useState(false);

  // Verificar se cadastro está aberto
  useEffect(() => {
    getSystemConfig().then(config => {
      if (config.cadastroAberto === false && !inviteEmail) {
        setCadastroFechado(true);
      }
    }).catch(() => {});
  }, [inviteEmail]);

  // Se veio com ?invite=email, preencher email e verificar convite
  useEffect(() => {
    if (inviteEmail) {
      setForm((p) => ({ ...p, email: inviteEmail }));
      verifyInvite(inviteEmail);
    }
  }, [inviteEmail]);

  const verifyInvite = async (email) => {
    setCheckingInvite(true);
    try {
      const inv = await checkInvite(email);
      setInvite(inv);
    } catch (err) {
      console.error('Erro ao verificar convite:', err);
    }
    setCheckingInvite(false);
  };

  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handlePhone = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 6) v = `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
    else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`;
    else if (v.length > 0) v = `(${v}`;
    setForm((p) => ({ ...p, telefone: v }));
  };

  // Verificar convite ao sair do campo email
  const handleEmailBlur = async () => {
    if (form.email && form.email.includes('@')) {
      await verifyInvite(form.email);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      await registerWithEmail(form.email, form.senha, {
        nome: form.nome,
        telefone: form.telefone,
        empresa: form.empresa,
        segmento: form.segmento,
        plano,
      });
      navigate('/dashboard');
    } catch (err) {
      setErro(
        err.code === 'auth/email-already-in-use'
          ? 'Este e-mail já está cadastrado.'
          : err.code === 'auth/weak-password'
          ? 'A senha deve ter no mínimo 6 caracteres.'
          : 'Erro ao criar conta. Tente novamente.'
      );
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setErro('');
    try {
      await loginWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      setErro('Erro ao continuar com Google.');
    }
  };

  const isInvite = !!invite;

  // Se cadastro está fechado, mostrar tela de bloqueio
  if (cadastroFechado) {
    return (
      <div style={{
        fontFamily: 'var(--font)', color: 'var(--text)', background: 'var(--surface)',
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 32,
      }}>
        <div style={{
          maxWidth: 440, textAlign: 'center', padding: '48px 32px',
          background: 'var(--white)', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 18,
            background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: '2rem',
          }}>🔒</div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 8px' }}>
            Cadastro temporariamente fechado
          </h2>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 24px' }}>
            No momento não estamos aceitando novos cadastros. Se você já tem uma conta, faça login normalmente.
          </p>
          <Link
            to="/login"
            style={{
              display: 'inline-block', padding: '12px 32px', borderRadius: 10,
              background: 'var(--accent)', color: 'var(--primary)',
              fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none',
            }}
          >
            Ir para Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'var(--font)', color: 'var(--text)', background: 'var(--surface)', minHeight: '100vh', display: 'flex' }}>
      {/* LEFT — Brand Panel */}
      <div style={{
        width: 480, minHeight: '100vh', background: 'var(--primary)', color: '#fff',
        padding: 48, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        position: 'fixed', left: 0, top: 0, bottom: 0, overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -150, right: -150, width: 500, height: 500, background: 'radial-gradient(circle, rgba(0,214,143,0.06) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -100, left: -100, width: 350, height: 350, background: 'radial-gradient(circle, rgba(245,166,35,0.04) 0%, transparent 60%)', pointerEvents: 'none' }} />

        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#fff', fontWeight: 700, fontSize: '1.3rem', position: 'relative', zIndex: 1 }}>
          <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.1)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--accent)', fontSize: '1.1rem' }}>S</div>
          SimulaBank
        </Link>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontFamily: "'Instrument Serif', serif", fontSize: '2.4rem', fontWeight: 400, lineHeight: 1.15, marginBottom: 16 }}>
            {isInvite ? (
              <>Você foi convidado para a <em style={{ color: 'var(--accent)', fontStyle: 'italic' }}>equipe</em></>
            ) : (
              <>Comece a fechar mais <em style={{ color: 'var(--accent)', fontStyle: 'italic' }}>contratos</em> hoje</>
            )}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', lineHeight: 1.65, maxWidth: 360 }}>
            {isInvite
              ? 'Crie sua senha para acessar o sistema. Seus dados já foram pré-configurados pelo administrador.'
              : 'Crie sua conta e tenha acesso ao simulador multibanco mais completo do mercado.'
            }
          </p>
          {!isInvite && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 36 }}>
              {FEATURES.map((f) => (
                <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(0,214,143,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', flexShrink: 0 }}>{f.icon}</div>
                  {f.text}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ position: 'relative', zIndex: 1, fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' }}>© 2026 SimulaBank. Todos os direitos reservados.</div>
      </div>

      {/* RIGHT — Form Panel */}
      <div style={{ marginLeft: 480, flex: 1, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 6 }}>
              {isInvite ? 'Complete seu cadastro' : 'Criar sua conta'}
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.92rem' }}>
              Já tem uma conta? <Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>Fazer login</Link>
            </p>
          </div>

          {/* Invite Banner */}
          {isInvite && (
            <div style={{
              padding: '14px 18px', borderRadius: 12, marginBottom: 24,
              background: 'rgba(0,214,143,0.06)', border: '1px solid rgba(0,214,143,0.15)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: '1.4rem' }}>🎉</span>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669' }}>
                  Convite encontrado
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  Papel: <strong>{ROLE_LABELS[invite.role] || invite.role}</strong>
                  {invite.displayName && <> · Nome: <strong>{invite.displayName}</strong></>}
                </div>
              </div>
            </div>
          )}

          {/* Checking invite */}
          {checkingInvite && (
            <div style={{
              padding: '10px 16px', borderRadius: 10, marginBottom: 16,
              background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)',
              fontSize: '0.78rem', color: '#3B82F6',
            }}>
              🔍 Verificando convite...
            </div>
          )}

          <GoogleButton label="Continuar com Google" onClick={handleGoogle} />
          <AuthDivider />

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Nome completo</label>
                <input required placeholder="João Silva" value={form.nome} onChange={handleChange('nome')} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Telefone</label>
                <input required placeholder="(61) 99999-9999" value={form.telefone} onChange={handlePhone} style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>E-mail {isInvite ? '' : 'corporativo'}</label>
              <input
                type="email" required
                placeholder="joao@suaempresa.com.br"
                value={form.email}
                onChange={handleChange('email')}
                onBlur={handleEmailBlur}
                readOnly={!!inviteEmail}
                style={{
                  ...inputStyle,
                  ...(inviteEmail ? { background: 'var(--surface)', cursor: 'not-allowed', color: 'var(--text-muted)' } : {}),
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Senha</label>
              <input type="password" required minLength={8} placeholder="Mínimo 8 caracteres" value={form.senha} onChange={handleChange('senha')} style={inputStyle} />
            </div>

            {/* Campos extras só para cadastro normal (não convite) */}
            {!isInvite && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Empresa</label>
                    <input placeholder="Nome da empresa" value={form.empresa} onChange={handleChange('empresa')} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Segmento</label>
                    <select value={form.segmento} onChange={handleChange('segmento')} style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">Selecione</option>
                      {SEGMENTOS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Planos */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 10 }}>Escolha seu plano</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {PLANOS.map((p) => {
                      const isSelected = plano === p.id;
                      return (
                        <div key={p.id} onClick={() => setPlano(p.id)} style={{
                          position: 'relative', padding: '14px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                          border: isSelected ? '2px solid var(--accent)' : '1.5px solid var(--border)',
                          background: isSelected ? 'rgba(0,214,143,0.04)' : 'var(--white)',
                          textAlign: 'center', transition: 'all 0.2s',
                        }}>
                          {p.popular && (
                            <span style={{
                              position: 'absolute', top: -8, right: -4,
                              background: 'var(--accent)', color: 'var(--primary)',
                              fontSize: '0.55rem', fontWeight: 700, padding: '2px 6px',
                              borderRadius: 100, textTransform: 'uppercase',
                            }}>Popular</span>
                          )}
                          <div style={{ fontSize: '1.2rem', marginBottom: 4 }}>{p.emoji}</div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 2 }}>{p.nome}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--accent)', fontWeight: 700 }}>{p.preco}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Erro */}
            {erro && (
              <p style={{ color: '#EF4444', fontSize: '0.82rem', textAlign: 'center', marginBottom: 12 }}>{erro}</p>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: 14, background: loading ? 'var(--border)' : 'var(--accent)',
              color: 'var(--primary)', border: 'none', borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font)', fontSize: '0.95rem', fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4,
              opacity: loading ? 0.7 : 1, transition: 'all 0.2s',
            }}>
              {loading ? 'Criando conta...' : isInvite ? 'Criar conta e entrar' : 'Criar conta — 7 dias grátis'}
            </button>

            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: 16, lineHeight: 1.5 }}>
              Ao criar sua conta, você concorda com os <a href="#" style={{ color: 'var(--text)', textDecoration: 'underline' }}>Termos de Uso</a> e a <a href="#" style={{ color: 'var(--text)', textDecoration: 'underline' }}>Política de Privacidade</a> do SimulaBank.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}